import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { usePatientRiskGapsWithUIExclusions } from '@ctw/shared/api/fhir/risk-gaps';
import { useFilteredSortedData } from '@ctw/shared/hooks/use-filtered-sorted-data';
import { useMemo } from 'react';
import { defaultRiskSort } from './helpers/sorts';
import { RiskGapView } from './helpers/views';

export interface PatientRiskGapsOverviewData {
  data: Array<RiskGapModel>;
  isLoading: boolean;
  hasNoRecords: boolean;
}

/**
 * Fetches the patient's risk gaps, narrows to the current calendar year, then
 * filters + sorts for the overview card. `hasNoRecords` reflects the raw fetch
 * so callers can distinguish "no risk gaps" from "none for the current year".
 */
export function usePatientRiskGapsOverviewData(): PatientRiskGapsOverviewData {
  const { data, isLoading } = usePatientRiskGapsWithUIExclusions();

  const currentYearRiskGaps = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.filter((riskGap) => {
      const periodYear = riskGap.period;
      return periodYear === new Date().getFullYear().toString();
    });
  }, [data]);

  const { data: filteredSortedData } = useFilteredSortedData({
    defaultSort: defaultRiskSort,
    viewOptions: [RiskGapView.active],
    records: currentYearRiskGaps,
    defaultView: RiskGapView.active.display,
  });

  return {
    data: filteredSortedData,
    isLoading,
    hasNoRecords: (data?.length ?? 0) === 0,
  };
}
